import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignOut, ArrowSquareOut } from "@phosphor-icons/react";
import useSEO from "@/hooks/useSEO";
import { Logo } from "@/components/Logo";
import { UploadForm } from "@/components/admin/UploadForm";
import { ReleaseRow } from "@/components/admin/ReleaseRow";
import { UpdateApiCard } from "@/components/admin/UpdateApiCard";
import { adminApi, clearToken, getToken } from "@/lib/adminApi";
import { formatNumber } from "@/lib/format";
import { API, BRAND_UPPER } from "@/lib/site";

const Stat = ({ label, value, testId }) => (
  <div className="glass rounded-2xl p-5 min-w-0">
    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
    <div className="font-display text-2xl sm:text-3xl font-bold mt-1 text-[#7DDCFF] truncate" data-testid={testId}>{value}</div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [releases, setReleases] = useState([]);
  const [stats, setStats] = useState(null);
  useSEO({ title: `Admin Dashboard – ${BRAND_UPPER}`, description: "Restricted administrator area.", path: "/admin", robots: "noindex, nofollow" });

  const logout = useCallback(() => {
    clearToken();
    nav("/admin/login", { replace: true });
  }, [nav]);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, st] = await Promise.all([
        adminApi.get("/admin/apks"),
        fetch(`${API}/apk/stats?t=${Date.now()}`, { cache: "no-store" }).then((r) => r.json()),
      ]);
      setReleases(list.releases);
      setStats(st);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) return logout();
    adminApi.get("/auth/me").then(({ data }) => { setUser(data); load(); }).catch(logout);
  }, [load, logout]);

  if (!user) return <main className="min-h-screen flex items-center justify-center text-slate-400" data-testid="admin-loading">Checking session…</main>;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b safe-top" style={{ borderColor: "rgba(255,255,255,.06)" }}>
        <div className="max-w-7xl mx-auto safe-x sm:px-8 h-16 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-slate-400 truncate max-w-[200px]" data-testid="admin-user-email">{user.email}</span>
            <Link to="/" className="btn-ghost !min-h-[44px] !py-2 !px-3 text-sm" aria-label="View site" data-testid="admin-view-site"><ArrowSquareOut size={16} aria-hidden="true" /><span className="hidden sm:inline">Site</span></Link>
            <button type="button" onClick={logout} className="btn-ghost !min-h-[44px] !py-2 !px-3 text-sm" aria-label="Log out" data-testid="admin-logout-btn"><SignOut size={16} aria-hidden="true" /><span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto safe-x sm:px-8 py-8 sm:py-10 safe-bottom">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Release control</h1>
        <section className="grid grid-cols-1 xs:grid-cols-3 gap-4 mt-6" aria-label="Statistics">
          <Stat label="Current LIVE" value={stats?.live_version ? `v${stats.live_version}` : "—"} testId="stat-live-version" />
          <Stat label="Total releases" value={formatNumber(stats?.total_releases)} testId="stat-total-releases" />
          <Stat label="Total downloads" value={formatNumber(stats?.total_downloads)} testId="stat-total-downloads" />
        </section>
        <div className="grid lg:grid-cols-12 gap-6 mt-8">
          <div className="lg:col-span-5 min-w-0"><UploadForm onUploaded={load} /><UpdateApiCard /></div>
          <section className="lg:col-span-7 min-w-0" aria-labelledby="history-heading">
            <h2 id="history-heading" className="font-display text-xl font-bold mb-4">Release history</h2>
            {releases.length === 0 ? (
              <p className="text-slate-400 text-sm glass rounded-2xl p-6" data-testid="releases-empty">No releases yet. Upload your first APK.</p>
            ) : (
              <ul className="space-y-3" data-testid="release-list">
                {releases.map((r) => <ReleaseRow key={r.id} r={r} onChange={load} />)}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
